import { Icona } from '../../components/Icona';
import { useEffect, useState } from 'react';
import { articoli, ETICHETTE_ORIGINE } from '../../api/articoli';
import type { OffertaConfrontata, SchedaArticolo as Scheda } from '../../api/articoli';
import { formattaData, formattaImporto, formattaPercentuale } from '../../lib/formato';

interface Props {
  articoloId: string;
  onChiudi: () => void;
}

export function SchedaArticolo({ articoloId, onChiudi }: Props) {
  const [scheda, setScheda] = useState<Scheda | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    let annullato = false;
    setScheda(null);
    setErrore(null);
    articoli
      .scheda(articoloId)
      .then((s) => !annullato && setScheda(s))
      .catch((e: Error) => !annullato && setErrore(e.message));
    return () => {
      annullato = true;
    };
  }, [articoloId]);

  useEffect(() => {
    const chiudiConEsc = (e: KeyboardEvent) => e.key === 'Escape' && onChiudi();
    window.addEventListener('keydown', chiudiConEsc);
    return () => window.removeEventListener('keydown', chiudiConEsc);
  }, [onChiudi]);

  if (errore) {
    return (
      <aside className="scheda">
        <p className="errore">{errore}</p>
        <button type="button" onClick={onChiudi}>
          Chiudi
        </button>
      </aside>
    );
  }

  if (!scheda) {
    return (
      <aside className="scheda">
        <p className="attesa">Carico la scheda…</p>
      </aside>
    );
  }

  const { confronto } = scheda;

  return (
    <aside className="scheda" aria-label={`Scheda articolo ${scheda.descrizione}`}>
      <header>
        <div>
          <p className="chiave">
            <strong>{scheda.siglaProduttore}</strong> {scheda.codiceProduttore}
            {scheda.produttore && <span className="produttore">{scheda.produttore}</span>}
          </p>
          <h2>{scheda.descrizione}</h2>
          <p className="riferimenti">
            {scheda.prezzoListino !== null ? (
              <>
                Listino {formattaImporto(scheda.prezzoListino)}
                {scheda.listinoAggiornatoAl && ` del ${formattaData(scheda.listinoAggiornatoAl)}`}
              </>
            ) : (
              // Non è la stessa cosa di «costa zero»: va detto.
              <em>Nessun listino produttore caricato</em>
            )}
            {scheda.unitaMisura && ` · ${scheda.unitaMisura}`}
          </p>
        </div>
        <button type="button" className="chiudi" onClick={onChiudi} aria-label="Chiudi la scheda">
          <Icona nome="chiudi" />
        </button>
      </header>

      <Verdetto confronto={confronto} />

      {confronto.confrontabili.length > 0 && (
        <table className="confronto">
          <thead>
            <tr>
              <th>Fornitore</th>
              <th className="destra">Netto</th>
              <th className="destra">In più</th>
              <th className="destra">Sul listino</th>
            </tr>
          </thead>
          <tbody>
            {confronto.confrontabili.map((offerta) => (
              <Riga key={offerta.id} offerta={offerta} />
            ))}
          </tbody>
        </table>
      )}

      {confronto.fuoriConfronto.length > 0 && (
        <details className="scartate">
          <summary>
            {confronto.fuoriConfronto.length === 1
              ? 'Una voce fuori dal confronto'
              : `${confronto.fuoriConfronto.length} voci fuori dal confronto`}
          </summary>
          <table className="confronto">
            <tbody>
              {confronto.fuoriConfronto.map((offerta) => (
                <tr key={offerta.id} className="fuori">
                  <td>{offerta.fornitore ?? offerta.codiceFornitoreMago}</td>
                  <td className="destra">{formattaImporto(offerta.costoNetto)}</td>
                  {/* Il motivo accanto alla riga: una voce sparita senza spiegazione
                      fa perdere più tempo di una scartata a voce alta. */}
                  <td className="motivo">{offerta.motivoEsclusione}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </aside>
  );
}

function Verdetto({ confronto }: { confronto: Scheda['confronto'] }) {
  const { migliore, forbice, confrontabili } = confronto;

  if (!migliore) {
    return (
      <p className="verdetto assente">
        Nessuna offerta valida oggi. Serve caricare un listino fornitore o una griglia sconti.
      </p>
    );
  }

  return (
    <p className="verdetto">
      Conviene da <strong>{migliore.fornitore ?? migliore.codiceFornitoreMago}</strong> a{' '}
      <strong>{formattaImporto(migliore.costoNetto)}</strong>
      {migliore.scontoSulListinoProduttore !== null && (
        <> ({formattaPercentuale(migliore.scontoSulListinoProduttore)} sul listino)</>
      )}
      {confrontabili.length > 1 && forbice !== null && (
        <>
          {' '}
          · su {confrontabili.length} offerte, tra la migliore e la peggiore ballano{' '}
          <strong>{formattaImporto(forbice)}</strong>
        </>
      )}
      .
    </p>
  );
}

function Riga({ offerta }: { offerta: OffertaConfrontata }) {
  return (
    <tr className={offerta.migliore ? 'migliore' : ''}>
      <td>
        <span className="nome">
          {offerta.fornitore ?? offerta.codiceFornitoreMago}
          {offerta.migliore && <em className="bollino">migliore</em>}
        </span>
        {/* Codice, origine e validità stanno sotto il nome invece che in tre colonne:
            in 520 pixel una tabella a sette colonne si legge male, e questi tre valori
            servono a spiegare la riga, non a essere confrontati fra righe. */}
        <span className="dettaglio">
          {offerta.codiceArticoloFornitore && <>{offerta.codiceArticoloFornitore} · </>}
          {ETICHETTE_ORIGINE[offerta.origine]}
          {offerta.scontoTesto && <em className="sconto">{offerta.scontoTesto}</em>}
          {' · dal '}
          {formattaData(offerta.validaDal)}
          {offerta.validaAl && ` al ${formattaData(offerta.validaAl)}`}
        </span>
      </td>
      <td className="destra costo">{formattaImporto(offerta.costoNetto)}</td>
      <td className="destra">
        {offerta.differenzaDallaMigliore === null || offerta.differenzaDallaMigliore === 0 ? (
          '—'
        ) : (
          <>
            +{formattaImporto(offerta.differenzaDallaMigliore)}
            {offerta.differenzaPercentuale !== null && (
              <small>+{formattaPercentuale(offerta.differenzaPercentuale)}</small>
            )}
          </>
        )}
      </td>
      <td className="destra">
        {offerta.scontoSulListinoProduttore === null
          ? '—'
          : formattaPercentuale(offerta.scontoSulListinoProduttore)}
      </td>
    </tr>
  );
}
